import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  ViewChild, inject, DestroyRef } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { CommentInputComponent } from '../comment-input/comment-input.component';
import { MenuAction } from '@anecdotes/design-system/dropdown-menu';
import { delayedPromise } from '@anecdotes/utils';
import { delay, filter } from 'rxjs/operators';
import { CommentPanelManagerService, FocusingService } from '../../services';
import { CommentInputValue, UserModel } from '@anecdotes/commenting';
import { CommentEntityNameEnum } from '../../models';
import { AuthService } from '@anecdotes/auth-core';
import { Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export const ShortDate = 'HH:mm MMM d, yyyy';

@Component({
  selector: 'app-comment-view',
  templateUrl: './comment-view.component.html',
  styleUrls: ['./comment-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentViewComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  @HostBinding('class')
  private readonly classes = 'flex flex-row gap-3 p-3 bg-white';

  private _body: string;

  @ViewChild('input', { static: true })
  private input: CommentInputComponent;

  @Input()
  date: string;

  get body(): string {
    return this._body;
  }

  @Input()
  set body(value: string) {
    this.textControl.setValue({ body: value } as CommentInputValue);
    this._body = value;
  }

  @Input()
  userId: string;

  @Input()
  commentId: string;

  @Input()
  commentType: 'thread' | 'comment';

  @Input()
  fallbackUserName: string;

  @Input()
  displayMenu;

  @Output()
  delete = new EventEmitter();

  @Output()
  edit = new EventEmitter<CommentInputValue>();

  textControl: UntypedFormControl = new UntypedFormControl(null, Validators.required);
  isEditable: boolean;
  isInputFocused: boolean;
  shortDate = ShortDate;
  usersMap$: Observable<Map<string, UserModel>>;

  menuActions: MenuAction[];

  constructor(
    private commentPanelManagerService: CommentPanelManagerService,
    private cd: ChangeDetectorRef,
    private focusingService: FocusingService,
    private elementRef: ElementRef<HTMLElement>,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.usersMap$ = this.commentPanelManagerService.getUsersMap();
    this.textControl.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.cd.detectChanges());

    this.commentPanelManagerService
      .getEditingThreadId()
      .pipe(
        filter(() => this.commentType === 'thread'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((threadId) => {
        if (threadId === this.commentId) {
          this.enableEditMode();
        } else {
          this.disableEditMode();
        }
      });

    this.commentPanelManagerService
      .getEditingCommentId()
      .pipe(
        filter(() => this.commentType === 'comment'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((commentId) => {
        if (commentId === this.commentId) {
          this.enableEditMode();
        } else {
          this.disableEditMode();
        }
      });

    if (this.commentType === 'comment') {
      this.focusingService
        .getFocusingStreamByResourceId(CommentEntityNameEnum.Comment, this.commentId)
        .pipe(delay(500), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.elementRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }
    this.initMenuActions();
  }

  confirmEdit(): void {
    this.edit.emit(this.textControl.value as CommentInputValue);
    this.commentPanelManagerService.closeEditing();
  }

  cancelEdit(): void {
    this.commentPanelManagerService.closeEditing();
  }

  buildTranslationKey(relativeKey: string): string {
    return `commenting.${relativeKey}`;
  }

  inputGotFocus(): void {
    this.isInputFocused = true;
    this.cd.detectChanges();
  }

  private async initMenuActions(): Promise<void> {
    const currentUser = await this.authService.getUserAsync();
    this.menuActions = [
      {
        id: `edit-${this.commentType}`,
        translationKey: 'commenting.options.edit',
        action: () => this.editMessage(),
        displayCondition: () => this.userId === currentUser.user_id,
      },
      {
        id: `delete-${this.commentType}`,
        translationKey: 'commenting.options.delete',
        action: () => this.deleteMessage(),
      },
    ];
  }

  private async enableEditMode(): Promise<void> {
    this.isEditable = true;
    this.cd.detectChanges();
    await delayedPromise(100);
    this.input.focus();
  }

  private disableEditMode(): void {
    this.isEditable = false;
    this.isInputFocused = false;
    this.textControl.setValue({ body: this.body } as CommentInputValue);
    this.cd.detectChanges();
  }

  private editMessage(): void {
    if (this.commentType === 'thread') {
      this.commentPanelManagerService.editThread(this.commentId);
    } else if (this.commentType === 'comment') {
      this.commentPanelManagerService.editComment(this.commentId);
    }
  }

  private deleteMessage(): void {
    this.delete.emit();
  }
}
